export const TIERS = ["low", "medium", "high", "critical"] as const;
export type Tier = (typeof TIERS)[number];

export const SIZES = ["xs", "s", "m", "l", "xl"] as const;
export type Size = (typeof SIZES)[number];

export const STAGES = [
  "backlog",
  "ready",
  "in_progress",
  "verifying",
  "review",
  "blocked",
  "done",
  "cancelled",
] as const;
export type Stage = (typeof STAGES)[number];

export const PRIORITIES = ["urgent", "high", "normal", "low"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const RISK_FLAGS = [
  "migration",
  "auth",
  "payments",
  "data_deletion",
  "infra",
  "public_api",
  "security",
  "new_dependency",
  /** An act outside the team (publish, send, restart). Every `action` task carries it (docs/specs/mission-control.md §3.2 rule 6). */
  "external_action",
] as const;
export type RiskFlag = (typeof RISK_FLAGS)[number];

/**
 * What a task delivers (docs/specs/mission-control.md §3.2). A separate axis from `TaskKind`.
 * Always `code` unless set explicitly; the project kind never chooses it, and the `no-code` label
 * does not change it (it stays a code task whose evidence needs no branchSha or tests).
 */
export const DELIVERABLES = ["code", "artifact", "check", "action"] as const;
export type Deliverable = (typeof DELIVERABLES)[number];

/** The Roomate-owned data a routine may read (docs/specs/routines.md §5.2). */
export const ROUTINE_READ_SCOPES = ["roomate:conversations", "roomate:activity", "roomate:tasks"] as const;
export type RoutineReadScope = (typeof ROUTINE_READ_SCOPES)[number];

/** The two cadence kinds sharing the schedules table; v1 routines use `daily` only. */
export const SCHEDULE_KINDS = ["interval", "daily"] as const;
export type ScheduleKind = (typeof SCHEDULE_KINDS)[number];

export const ROUTINE_STATUSES = ["active", "paused", "paused_failing"] as const;
export type RoutineStatus = (typeof ROUTINE_STATUSES)[number];

export const ROUTINE_RUN_TRIGGERS = ["schedule", "manual"] as const;
export type RoutineRunTrigger = (typeof ROUTINE_RUN_TRIGGERS)[number];

export const ROUTINE_PROPOSAL_STATES = ["open", "created", "dismissed", "snoozed"] as const;
export type RoutineProposalState = (typeof ROUTINE_PROPOSAL_STATES)[number];

/** Only `code` work lands as a git branch; every other kind has no branch and runs in a scratch dir (mission-control §3.2, C1, C2). A task without a deliverable is code. */
export function needsBranch(deliverable: Deliverable | null | undefined): boolean {
  return (deliverable ?? "code") === "code";
}

export const ROLES = ["founder", "manager", "tech_lead", "pm", "developer", "backend", "frontend", "qa", "ux", "ui", "other", "chief"] as const;
export type Role = (typeof ROLES)[number];

/** `chief` is the governance anchor of the Chief of Office (docs/specs/chief-of-office.md §4): only `POST /api/office/chief` sets it, so role pickers offer these instead. */
export const ASSIGNABLE_ROLES = ROLES.filter((r) => r !== "chief") as readonly Exclude<Role, "chief">[];

/** The explicit role check that keeps the Chief out of builder and reviewer candidacy: its empty `allowedAreas` alone would not (`areasFit` treats an empty fence as "anywhere"). */
export function isChief(actor: { role: Role }): boolean {
  return actor.role === "chief";
}

export const CHIEF_STATES = ["ready", "working", "offline", "paused", "capped"] as const;
export type ChiefState = (typeof CHIEF_STATES)[number];
/** What any member or viewer may read about the Chief (`GET /api/office/chief`); never a token. Roomie's `facts.chief` is built from it. */
export type ChiefStatusDTO = { state: ChiefState; name: string };

/** C8 (§11): what an admin or member reads about the Chief's guards (`GET /api/office/chief/guards`). The Chief itself is refused this route. */
export type ChiefGuardsDTO = {
  dailyCapUsd: number;
  spentTodayUsd: number;
  /** The next 00:00 UTC, when the day's spend starts again. */
  resetsAt: number;
  /** Why the breaker is open, or null. */
  pausedReason: string | null;
  runsLastHour: number;
  limits: { runBudgetUsd: number; runMaxTurns: number; runsPerHour: number };
};

export const ACCESS = ["admin", "member", "viewer", "agent"] as const;
export type Access = (typeof ACCESS)[number];

export const RUNTIMES = ["claude-code", "cursor", "codex", "antigravity", "vscode", "other"] as const;
export type Runtime = (typeof RUNTIMES)[number];

/** Where an agent runs. `runtime` keeps meaning which CLI; `host` means where it runs.
 *  Unset or unrecognised values always read back as "local" (see rows.ts toActor), so older
 *  nodes and rows written before this field existed are unaffected. */
export const ACTOR_HOSTS = ["local", "vercel-sandbox"] as const;
export type ActorHost = (typeof ACTOR_HOSTS)[number];

/**
 * A node's self-reported Claude Code login state on its heartbeat (#110): "ok" when `claude auth
 * status` reports a subscription credential (code.claude.com/docs/en/cli-reference, checked
 * 2026-10-10 — `authMethod` is one of none/claude.ai/oauth_token/api_key/api_key_helper/third_party,
 * with no "expired" field), "missing" when it reports none at all, "expired" when a run actually
 * hit a reactive "login expired" failure after a prior check reported a subscription method (see
 * `agent/login-check.ts`). Used to raise a Re-connect item for a cloud agent whose sandbox lost its
 * login. Omitting it on the heartbeat is valid (a merge-only node runs no agents and has nothing to
 * report), and an older node that never sends it reads back as having nothing to report.
 */
export const CLOUD_LOGIN_STATES = ["ok", "missing", "expired"] as const;
export type CloudLoginState = (typeof CLOUD_LOGIN_STATES)[number];

/**
 * Where a cloud agent's sandbox can be: pending (not yet running a session), running, on its way down
 * (stopping, including the provider's "snapshotting" state), stopped, or failed.
 */
export const HOST_STATES = ["pending", "running", "stopping", "stopped", "failed"] as const;
export type HostState = (typeof HOST_STATES)[number];

export const MODES = ["agent", "agent_with_review", "human"] as const;
export type Mode = (typeof MODES)[number];

export const DECISION_KINDS = [
  "approve_spec",
  "review_change",
  "question",
  "exception",
  "risk_acceptance",
  "spot_check",
  "human_action",
] as const;
export type DecisionKind = (typeof DECISION_KINDS)[number];

export type SkillLevel = { key: string; level: number };

export type ActorKind = "human" | "agent";

export const PERMISSION_MODES = ["default", "acceptEdits"] as const;
export type PermissionMode = (typeof PERMISSION_MODES)[number];

/** "seat": the owner's Claude Code login (OAuth/keychain). "api_key": the node's ANTHROPIC_API_KEY via `--bare`, for shared always-on agents. */
export const AUTH_MODES = ["seat", "api_key"] as const;
export type AuthMode = (typeof AUTH_MODES)[number];

/** The non-Claude runtimes an agent may fall back to, in `agentConfig.fallback.runtimes` order (docs/specs/runtime-fallback.md §4.2). */
export const FALLBACK_RUNTIMES = ["codex", "cursor", "antigravity"] as const;
export type FallbackRuntime = (typeof FALLBACK_RUNTIMES)[number];

/** Opt-in runtime fallback for one agent (§4.2): off unless `enabled`; the order is the order of `runtimes`. */
export type AgentFallbackConfig = { enabled: boolean; runtimes: FallbackRuntime[] };

/** "auto" (default): the runner picks a model per task via `modelForTask` (see model.ts). "pinned": always `agentConfig.model`, whatever the task. */
export const MODEL_POLICIES = ["auto", "pinned"] as const;
export type ModelPolicy = (typeof MODEL_POLICIES)[number];

export type McpServerConfig =
  | { type: "http"; url: string; headers?: Record<string, string> }
  | { type: "stdio"; command: string; args?: string[]; env?: Record<string, string> };

/** A subagent the main agent can delegate to (Claude Code `--agents`). */
export type SubagentConfig = {
  description: string;
  prompt: string;
  model: string;
  tools?: string[];
};

/** How a node runs this agent. Secrets must be `${ENV_VAR}` references, never values. */
export type AgentConfig = {
  model: string | null;
  /**
   * "auto" (default): ignore `model` and let `modelForTask` pick per run. "pinned": always use
   * `model`. Optional, unlike this type's other fields: every *stored* AgentConfig is read through
   * `{...DEFAULT_AGENT_CONFIG, ...stored}` (rows.ts), which already fills this in, but a hand-built
   * literal elsewhere (e.g. apps/web's import-preview mock data) may omit it and still typecheck —
   * `modelFor` (apps/server/src/agent/run-config.ts) treats a missing value the same as "auto".
   */
  modelPolicy?: ModelPolicy;
  /** Stronger model consulted at decision points (Claude Code advisor tool). Null = off. */
  advisorModel: string | null;
  /** Named subagents with their own model, e.g. a cheap researcher and an independent reviewer. */
  subagents: Record<string, SubagentConfig>;
  /** Null = no cap. Runs bill against the owner's Claude subscription, so the default is none. */
  maxBudgetUsd: number | null;
  maxTurns: number | null;
  permissionMode: PermissionMode;
  allowedTools: string[];
  deniedTools: string[];
  mcpServers: Record<string, McpServerConfig>;
  instructions: string;
  /** How this agent authenticates to Claude Code. The key itself is never part of this config; it comes from the node's own environment at run time. */
  auth: AuthMode;
  /** Set when this agent was created or last re-imported from a Claude Code folder. Null for a hand-built agent, so existing rows parse unchanged. */
  imported: ImportedMeta | null;
  /** Runtime fallback (docs/specs/runtime-fallback.md §4.2). Optional: absent = off, so stored configs parse unchanged. */
  fallback?: AgentFallbackConfig;
  /** A custom adapter this agent runs on (docs/specs/onboarding-teammates.md §7.4): the id and the definition hash pinned from the node the owner picked. Only meaningful with `runtime: "other"`. */
  customAdapter?: CustomAdapterRef | null;
};

export type CustomAdapterRef = { id: `custom:${string}`; definitionHash: string };

/** Points an agent's config back at the import that produced it (see `@roomate/core/agent-import`). */
export type ImportedMeta = {
  importId: string;
  version: number;
  bundleHash: string;
  addDir: boolean;
  hooks?: Record<string, unknown>;
  env?: Record<string, string>;
};

export const DEFAULT_SUBAGENTS: Record<string, SubagentConfig> = {
  researcher: {
    description: "Fast read-only research: find files, read docs and code, summarize what matters. Use before changing unfamiliar code.",
    prompt: "You are a researcher. Find and read what is needed, then report concise findings with file paths and sources. Never edit files.",
    model: "haiku",
    tools: ["Read", "Grep", "Glob", "WebFetch", "WebSearch"],
  },
  "test-runner": {
    description: "Runs the test, typecheck and build commands and reports exact pass/fail counts and failures. Use before submitting evidence.",
    prompt: "You run the project's checks exactly as asked and report the commands, pass/fail counts, and the first failure output verbatim. Never edit files.",
    model: "haiku",
    tools: ["Bash", "Read", "Grep", "Glob"],
  },
  reviewer: {
    description: "Independent code reviewer for the finished change. Use before submit_evidence; its findings go into the evidence review.",
    prompt: "You are an independent senior reviewer. Read the diff against the base branch and the acceptance criteria. Report findings as critical/important/minor with file:line. Be concrete; no praise.",
    model: "sonnet",
    tools: ["Read", "Grep", "Glob", "Bash"],
  },
};

export const DEFAULT_AGENT_CONFIG: AgentConfig = {
  model: null,
  modelPolicy: "auto",
  advisorModel: "opus",
  subagents: DEFAULT_SUBAGENTS,
  maxBudgetUsd: null,
  maxTurns: null,
  permissionMode: "acceptEdits",
  // Headless runs can't answer a permission prompt, so anything not listed is denied outright.
  // Read-only and in-worktree file commands are listed so agents don't stall on `ls` or `mkdir`.
  allowedTools: [
    "Bash(git *)", "Bash(pnpm *)", "Bash(npm *)", "Bash(npx *)", "Bash(node *)", "Bash(pytest *)", "Bash(make *)",
    "Bash(ls *)", "Bash(cat *)", "Bash(head *)", "Bash(tail *)", "Bash(wc *)", "Bash(find *)", "Bash(grep *)", "Bash(rg *)",
    "Bash(diff *)", "Bash(cd *)", "Bash(mkdir *)", "Bash(cp *)", "Bash(mv *)", "Bash(touch *)", "Bash(sed -n *)", "Bash(sort *)", "Bash(jq *)",
  ],
  deniedTools: ["Bash(git push --force *)", "Bash(git push -f *)", "Read(./.env)", "Read(./.env.*)"],
  mcpServers: {},
  instructions: "",
  auth: "seat",
  imported: null,
};

export type Actor = {
  id: string;
  kind: ActorKind;
  name: string;
  access: Access;
  role: Role;
  skills: SkillLevel[];
  focus: string[];
  runtime: Runtime | null;
  /** Where this agent runs. Always a valid ActorHost coming out of the row mapper, defaulting to "local". */
  host: ActorHost;
  /** This agent's cloud sandbox name, set once on first provision so a later rename doesn't orphan it (and the Claude login inside it). Null until provisioned, and for `host: "local"`. */
  hostSandbox: string | null;
  ownerId: string | null;
  /** A person's reports-to: an active human, or null/absent for nobody. Display only (routing, review assignment and decision targets ignore it). Always null for an agent. */
  managerId?: string | null;
  allowedAreas: string[];
  maxParallel: number;
  /** Static human builder opt-out. Review-only people stay eligible to review; agents use maxParallel. */
  canBuild?: boolean;
  active: boolean;
  /**
   * A person an admin removed. They cannot sign in, hold no live token, and stay out of routing,
   * the room, pickers, mentions and the Bridge. Past decisions, reviews and provenance still name them.
   * Absent on older rows and hand-built fixtures; that means not removed.
   */
  archived?: boolean;
  agentConfig: AgentConfig | null;
  /** Assigned projects; [] = all projects. Required so an actor built without it fails to compile instead of silently gaining every project. Only agents are filtered by it. */
  projectIds: string[];
};

export type AcceptanceCriterion = { id: string; text: string };

export type TaskKind = "feature" | "bug" | "chore";

export type RiskAssessment = { tier: Tier; points: number; reasons: string[] };

export type RoutableTask = {
  id: string;
  projectId: string;
  title: string;
  description: string;
  kind: TaskKind;
  areas: string[];
  labels: string[];
  skills: string[];
  size: Size;
  flags: RiskFlag[];
  priority: Priority;
  /** Absent means `code`. */
  deliverable?: Deliverable;
};

export const RULE_EFFECTS = [
  "forbid_agent_builder",
  "require_human_reviewer",
  "require_focus",
  "min_tier",
  "require_reviewer",
] as const;
export type RuleEffect = (typeof RULE_EFFECTS)[number];

export type Rule = {
  id: string;
  name: string;
  projectId: string | null;
  match: { areas?: string[]; labels?: string[]; kinds?: TaskKind[] };
  effect: RuleEffect;
  value: string | null;
  enabled: boolean;
};

export function tierIndex(tier: Tier): number {
  return TIERS.indexOf(tier);
}

export function maxTier(a: Tier, b: Tier): Tier {
  return tierIndex(a) >= tierIndex(b) ? a : b;
}
